import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type Tone =
  | 'neutral'
  | 'primary'
  | 'accent'
  | 'positive'
  | 'negative'
  | 'info'
  | 'warning'
  /** Product-area chips (ARCHITECTURE §11): blue = AI & data, saffron = reviews, teal = messages. */
  | 'ai'
  | 'reviews'
  | 'messages';

const tones: Record<Tone, string> = {
  neutral: 'bg-sunken text-ink-soft border-hairline',
  primary: 'bg-primary-soft text-primary-ink border-primary/15',
  accent: 'bg-accent-soft text-accent-ink border-accent/25',
  positive: 'bg-success-soft text-success-ink border-success/15',
  negative: 'bg-negative-soft text-negative-ink border-negative/15',
  info: 'bg-info-soft text-info-ink border-info/15',
  warning: 'bg-warning-soft text-warning-ink border-warning/25',
  ai: 'bg-area-ai-soft text-area-ai-ink border-area-ai/20',
  reviews: 'bg-area-reviews-soft text-area-reviews-ink border-area-reviews/35',
  messages: 'bg-area-messages-soft text-area-messages-ink border-area-messages/20',
};

const dots: Record<Tone, string> = {
  neutral: 'bg-ink-subtle',
  primary: 'bg-primary',
  accent: 'bg-accent',
  positive: 'bg-success',
  negative: 'bg-negative',
  info: 'bg-info',
  warning: 'bg-warning',
  ai: 'bg-area-ai',
  reviews: 'bg-area-reviews',
  messages: 'bg-area-messages',
};

export function Badge({
  tone = 'neutral',
  className,
  icon,
  children,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & { tone?: Tone; icon?: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex h-5.5 items-center gap-1 whitespace-nowrap rounded-[4px] border px-1.5 text-xs font-semibold [&_svg]:size-3.5',
        tones[tone],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </span>
  );
}

/**
 * Semantic status with a dot. Map domain states to tones in one place per feature,
 * e.g. review request: sent→info, google_clicked→positive, disliked→negative.
 */
export function StatusPill({
  tone = 'neutral',
  pulse,
  className,
  children,
}: {
  tone?: Tone;
  pulse?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-ink-soft',
        className,
      )}
    >
      <span className="relative inline-flex size-2">
        {pulse && (
          <span
            className={cn(
              'absolute inset-0 animate-ping rounded-full opacity-60 motion-reduce:hidden',
              dots[tone],
            )}
          />
        )}
        <span
          className={cn('relative inline-flex size-2 rounded-full ring-2 ring-surface', dots[tone])}
        />
      </span>
      {children}
    </span>
  );
}

/**
 * The "Pro" mark in navigation and next to Pro-only controls (A3: saffron marks the Pro badge;
 * never italic). `solid` (default): saffron tag with dark ink, for page headers and upsells.
 * `outline`: saffron text on a thin saffron edge, for lists where many items repeat it (the
 * sidebar on the blue field), so a column of solid saffron tags does not drown the one number
 * that needs attention.
 */
export function ProMark({
  className,
  variant = 'solid',
}: {
  className?: string;
  variant?: 'solid' | 'outline';
}) {
  return (
    <span
      data-variant={variant}
      className={cn(
        'inline-flex h-4.5 items-center gap-0.5 rounded-[4px] px-1.5 text-[0.625rem] font-extrabold uppercase leading-none tracking-[0.06em]',
        variant === 'solid'
          ? 'bg-accent text-on-accent'
          : 'text-accent-ink shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--gh-accent)_70%,transparent)]',
        className,
      )}
    >
      <svg viewBox="0 0 10 10" className="size-2" aria-hidden>
        <path
          d="M5 .6l1.3 2.8 3 .3-2.3 2 .7 3L5 7.2 2.3 8.7l.7-3L.7 3.7l3-.3z"
          fill="currentColor"
        />
      </svg>
      Pro
    </span>
  );
}
